import { PAGES } from "@dune/types";
import type { SubmissionParticipantWithValidDecennialCertificate } from "@dune/types";
import { useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAddBuilderParticipantToReception } from "../../../../api/mutations/constructionSiteSubmission.mutations";
import { useGetConstructionSiteReceptionParticipants } from "../../../../api/queries/constructionSiteSubmission.queries";
import { BuilderToSubmissionForm } from "../../../../component/constructionSites/submission/participants/BuilderToSubmissionForm";
import PageLayoutWithTabs from "../../../../component/layout/PageLayoutWithTabs";
import { useBuilderToSubmissionForm } from "../../../../hooks/useParticipantToSubmissionForm";
import { ParticipantDocumentsReceptionTable } from "../../components/intervenants/ParticipantDocumentsReceptionTable";

const AddBuilderReceptionPage = () => {
	const navigate = useNavigate();
	const { constructionSiteId } = useParams<{ constructionSiteId: string }>();
	const [searchParams] = useSearchParams();
	const returnUrl = searchParams.get("returnUrl");

	const { data: receptionParticipants } =
		useGetConstructionSiteReceptionParticipants(Number(constructionSiteId));
	// Fees now come from reception participants

	const firstBuilder = useMemo(() => {
		const rp = receptionParticipants?.find((p) => p.type === "BUILDER");
		if (!rp) return null;
		const isNewParticipant = !rp.isFromSubmission;
		const participantWithFlag = {
			...rp.participant,
			isNewForReception: isNewParticipant,
		};

		const obj = {
			id: rp.id,
			type: rp.type,
			designerType: rp.designerType ?? null,
			totalFeesHT: rp.totalFeesHT ?? 0,
			totalFeesTTC: rp.totalFeesTTC ?? 0,
			totalMissionsOrActivitiesOrMaterialTypes: [],
			participant: participantWithFlag,
			decennialCertificatesOfSubmissionOrValidFromOthers: null,
			insurer: null,
		} satisfies SubmissionParticipantWithValidDecennialCertificate;
		return obj;
	}, [receptionParticipants]);

	const goBack = () => {
		if (returnUrl) navigate(returnUrl);
		else if (constructionSiteId)
			navigate(PAGES.PROJECTS.reception.withParams(constructionSiteId, 3));
		else navigate(PAGES.PROJECTS.index);
	};

	const numericConstructionSiteId = constructionSiteId
		? Number(constructionSiteId)
		: 0;

	const addBuilder = useAddBuilderParticipantToReception(
		numericConstructionSiteId,
	);

	const formApi = useBuilderToSubmissionForm({
		mode: "add",
		onSubmit: (data) =>
			addBuilder.mutate(data, {
				onSuccess: () => goBack(),
			}),
		constructionSiteId: numericConstructionSiteId,
	});

	if (!constructionSiteId) return null;

	return (
		<PageLayoutWithTabs title="Ajouter un réalisateur">
			<FormProvider {...formApi.form}>
				<form onSubmit={formApi.handleSubmit}>
					<BuilderToSubmissionForm
						constructionSiteId={numericConstructionSiteId}
						onSubmit={formApi.handleSubmit}
						onCancel={goBack}
						isSubmitting={addBuilder.isPending}
						documentsSlot={
							firstBuilder && (
								<ParticipantDocumentsReceptionTable
									submissionParticipant={firstBuilder}
									constructionSiteId={numericConstructionSiteId}
								/>
							)
						}
					/>
				</form>
			</FormProvider>
		</PageLayoutWithTabs>
	);
};

export default AddBuilderReceptionPage;
